<script lang="ts">
import {
  defineComponent, computed, PropType, ref, onBeforeMount, watch,
} from 'vue';

import { useApi, TrainingConfigs } from 'dive-common/apispec';
import JobLaunchDialog from 'dive-common/components/JobLaunchDialog.vue';
import ImportButton from 'dive-common/components/ImportButton.vue';
import { summarizeTrainingSplits } from 'dive-common/trainingSplit';
import { useRequest } from 'dive-common/use';
import { simplifyTrainingName } from 'dive-common/constants';
import { useBrand } from 'platform/web-girder/store/useBrand';
import { useConfig } from 'platform/web-girder/store/useConfig';

export default defineComponent({
  name: 'RunTrainingMenu',

  components: { JobLaunchDialog, ImportButton },

  props: {
    selectedDatasetIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    /** Training split per selected dataset, aligned with selectedDatasetIds. */
    datasetSplits: {
      type: Array as PropType<(string | null)[]>,
      default: () => [],
    },
    buttonOptions: {
      type: Object,
      default: () => ({}),
    },
    menuOptions: {
      type: Object,
      default: () => ({}),
    },
  },

  setup(props) {
    const { brandData } = useBrand();
    const { getTrainingConfigurations, runTraining } = useApi();
    const { jobsDisabled, jobsDisabledMessage } = useConfig();

    const trainingConfigurations = ref<TrainingConfigs | null>(null);
    const selectedTrainingConfig = ref<string | null>(null);
    const annotatedFramesOnly = ref<boolean>(false);
    const fineTuning = ref<boolean>(false);
    const selectedFineTune = ref<string>('');
    const {
      request: _runTrainingRequest,
      reset: dismissJobDialog,
      state: jobState,
    } = useRequest();

    const successMessage = computed(() => `Started training on ${props.selectedDatasetIds.length} dataset(s)`);
    const splitSummary = computed(() => summarizeTrainingSplits(props.datasetSplits));

    const fineTuneModelList = computed(() => {
      const modelList: {text: string, type: 'user' | 'system', name: string}[] = [];
      if (trainingConfigurations.value?.models) {
        Object.entries(trainingConfigurations.value.models)
          .forEach(([, value]) => {
            modelList.push({
              text: `${value.name} - ${value.folderId ? 'User' : 'System'} Model`,
              type: value.folderId ? 'user' : 'system',
              name: value.name,
            });
          });
      }
      modelList.sort((a, b) => b.type.localeCompare(a.type));
      return modelList;
    });
    const selectedFineTuneObject = computed(() => {
      if (selectedFineTune.value !== '' && trainingConfigurations.value?.models) {
        return Object.values(trainingConfigurations.value.models)
          .find((model) => model.name === selectedFineTune.value);
      }
      return undefined;
    });
    onBeforeMount(async () => {
      const resp = await getTrainingConfigurations();
      trainingConfigurations.value = resp;
      selectedTrainingConfig.value = resp.training.default;
    });

    const trainingDisabled = computed(() => (
      props.selectedDatasetIds.length === 0 || jobsDisabled.value
    ));
    const trainingTooltip = computed(() => (
      jobsDisabled.value
        ? (jobsDisabledMessage.value || 'Jobs are temporarily disabled')
        : 'Train a detector model on this data'
    ));
    const trainingOutputName = ref<string | null>(null);
    const menuOpen = ref(false);
    const labelText = ref<string>('');
    const labelFile = ref<File>();

    async function runTrainingOnFolder() {
      const outputPipelineName = trainingOutputName.value;
      if (trainingDisabled.value || !outputPipelineName || jobsDisabled.value) {
        return;
      }
      await _runTrainingRequest(() => {
        if (!trainingConfigurations.value || !selectedTrainingConfig.value) {
          throw new Error('Training configurations not found.');
        }
        if (labelText.value) {
          return runTraining(
            props.selectedDatasetIds,
            outputPipelineName,
            selectedTrainingConfig.value,
            annotatedFramesOnly.value,
            labelText.value,
            selectedFineTuneObject.value,
          );
        }
        return runTraining(
          props.selectedDatasetIds,
          outputPipelineName,
          selectedTrainingConfig.value,
          annotatedFramesOnly.value,
          undefined,
          selectedFineTuneObject.value,
        );
      });
      menuOpen.value = false;
      trainingOutputName.value = null;
    }

    watch(labelFile, () => {
      if (labelFile.value) {
        const reader = new FileReader();
        reader.onload = (evt) => {
          labelText.value = evt.target?.result as string;
        };
        reader.readAsText(labelFile.value);
      }
    });

    const clearLabelText = () => {
      labelText.value = '';
    };

    return {
      brandData,
      trainingConfigurations,
      selectedTrainingConfig,
      annotatedFramesOnly,
      trainingOutputName,
      menuOpen,
      trainingDisabled,
      trainingTooltip,
      jobsDisabled,
      jobState,
      successMessage,
      splitSummary,
      dismissJobDialog,
      runTrainingOnFolder,
      labelFile,
      clearLabelText,
      simplifyTrainingName,
      // Fine-Tuning
      fineTuning,
      fineTuneModelList,
      selectedFineTune,
    };
  },
});
</script>

<template>
  <div>
    <v-menu
      v-model="menuOpen"
      max-width="500"
      v-bind="menuOptions"
      :close-on-content-click="false"
      :disabled="jobsDisabled"
    >
      <template #activator="{ on: menuOn }">
        <v-tooltip
          bottom
          :open-delay="250"
          :disabled="menuOptions.offsetX && !jobsDisabled"
        >
          <template #activator="{ on: tooltipOn }">
            <!-- Wrapper keeps tooltip working when the button is disabled -->
            <span
              class="d-inline-block"
              style="width: 100%"
              v-on="tooltipOn"
            >
              <v-btn
                v-bind="buttonOptions"
                :disabled="trainingDisabled || buttonOptions.disabled"
                v-on="jobsDisabled ? {} : menuOn"
              >
                <v-icon>
                  mdi-brain
                </v-icon>
                <span
                  v-show="!$vuetify.breakpoint.mdAndDown || buttonOptions.block"
                  class="pl-1"
                >
                  Run Training
                </span>
                <v-spacer />
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </span>
          </template>
          <span>{{ trainingTooltip }}</span>
        </v-tooltip>
      </template>

      <template>
        <v-card
          v-if="trainingConfigurations"
          outlined
          class="training-menu"
        >
          <v-card-title class="pb-1">
            Run Training
          </v-card-title>

          <v-card-text>
            <p>
              Specify the name of the resulting pipeline
              and configuration file to use for training.
              Check the
              <a href="https://kitware.github.io/dive/Pipeline-Documentation/#training">
                documentation
              </a>
              for more information about these options.
            </p>
            <v-alert
              v-if="brandData.trainingMessage"
              dense
              color="warning"
              outlined
            >
              {{ brandData.trainingMessage }}
            </v-alert>

            <v-text-field
              v-model="trainingOutputName"
              outlined
              class="my-4"
              label="New Model Name"
              hint="Choose a name for the newly trained model"
              persistent-hint
            />
            <v-select
              v-if="trainingConfigurations.training.configs.length > 0"
              v-model="selectedTrainingConfig"
              outlined
              class="my-4"
              label="Configuration File"
              :items="trainingConfigurations.training.configs"
              item-text="name"
              item-value="name"
              :hint="selectedTrainingConfig"
              persistent-hint
            >
              <template #item="{ item, on, attrs }">
                <v-tooltip
                  left
                  :open-delay="250"
                  :disabled="!item.description"
                  max-width="300"
                  content-class="pipeline-description-tooltip"
                >
                  <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                    <v-list-item
                      v-bind="{ ...attrs, ...tooltipAttrs }"
                      v-on="{ ...on, ...tooltipOn }"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ simplifyTrainingName(item.name || item) }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                  <span>{{ item.description }}</span>
                </v-tooltip>
              </template>
              <template #selection="{ item }">
                {{ simplifyTrainingName(item.name || item) }}
              </template>
            </v-select>
            <v-file-input
              v-model="labelFile"
              icon="mdi-folder-open"
              label="Labels.txt mapping file (optional)"
              hint="Combine or rename output classes using a labels.txt file"
              persistent-hint
              clearable
              @click:clear="clearLabelText"
            />
            <v-checkbox
              v-model="annotatedFramesOnly"
              label="Use annotated frames only"
              hint="Train only on frames with groundtruth and ignore frames without annotations"
              persistent-hint
              class="pt-0"
            />
            <v-checkbox
              v-model="fineTuning"
              label="Fine Tune Model"
              hint="Fine Tune an existing model"
              persistent-hint
              class="pt-0"
            />
            <v-select
              v-if="fineTuning"
              v-model="selectedFineTune"
              outlined
              class="my-4"
              label="Fine Tune Model"
              :items="fineTuneModelList"
              item-value="name"
              item-text="text"
              hint="Model to Fine Tune"
              persistent-hint
            />
            <div
              v-if="splitSummary.labeled"
              class="text-caption grey--text mt-2"
            >
              Split: {{ splitSummary.text }}. Unlabeled datasets train; validation datasets
              are held out to monitor training; test datasets are scored once training finishes.
            </div>
            <v-alert
              v-if="selectedDatasetIds.length > 0 && !splitSummary.trainable"
              dense
              outlined
              type="warning"
              class="mt-2"
            >
              Every selected dataset is labeled validation or test; label at least one
              as Train or clear its split.
            </v-alert>
            <v-btn
              depressed
              block
              color="primary"
              class="mt-4"
              :disabled="!trainingOutputName || !selectedTrainingConfig || !splitSummary.trainable"
              @click="runTrainingOnFolder"
            >
              Train on {{ selectedDatasetIds.length }} dataset(s)
            </v-btn>
          </v-card-text>
        </v-card>
      </template>
    </v-menu>
    <JobLaunchDialog
      :value="jobState.count > 0"
      :loading="jobState.loading"
      :error="jobState.error ?? undefined"
      :message="successMessage"
      @close="dismissJobDialog"
    />
  </div>
</template>

<style lang="css" scoped>
.training-menu {
  max-height: 90vh;
  overflow-y: auto;
}
</style>
